/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { useState } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from '@/lib/utils';
import { faCalendarStar, faXmark } from '@fortawesome/pro-regular-svg-icons';
import type { SizeProp } from '@fortawesome/fontawesome-svg-core';
import { MarkierungType } from '@/domain/dienstplan/shared/types/Markierung';
import { markierungMapper } from '@/utils/markierung';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import { Variant } from '@/domain/termine/shared/types/TerminImage';
import { usePopoverDirection } from '@/domain/termine/shared/utils/getPopoverDirection';

export interface TerminImageProps extends React.ComponentProps<typeof Avatar> {
  bildUrl?: string;
  bildName?: string;
  markierung?: MarkierungType;
  variant?: Variant;
  expandable?: boolean;
}

export function TerminImage({
  bildUrl,
  bildName,
  markierung,
  variant,
  expandable = true,
  ...avatarProps
}: TerminImageProps) {
  const [isOpen, setIsOpen] = useState(false);
  const sizeClasses: Record<string, string> = {
    detail: 'w-16 h-16',
    sheet: 'w-24 h-24',
    default: 'w-6 h-6',
  };
  const sizeClass = sizeClasses[variant] || sizeClasses.default;

  const iconSizes: Record<string, SizeProp> = {
    detail: '2x',
    sheet: '2x',
    default: 'sm',
  };
  const iconSize: SizeProp = iconSizes[variant] || iconSizes.default;

  const popoverDirection = usePopoverDirection();

  const avatar = (
    <Avatar
      {...avatarProps}
      className={`${sizeClass} border-2 border-[--border-color] cursor-pointer ${
        avatarProps.className ?? ''
      }`}
      style={{
        borderColor: markierung ? markierungMapper[markierung].base : '',
        ...avatarProps.style,
      }}
      onClick={() => {
        if (expandable && bildUrl) setIsOpen(true);
      }}>
      {bildUrl ? (
        <AvatarImage src={bildUrl} alt={bildName} className="object-cover" />
      ) : (
        <AvatarFallback
          className="bg-transparent"
          onClick={(e) => e.stopPropagation()}>
          <FontAwesomeIcon
            icon={faCalendarStar}
            size={iconSize}
            className="text-neutrals-N800"
          />
        </AvatarFallback>
      )}
    </Avatar>
  );
  if (variant === 'list') return avatar;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>{avatar}</PopoverTrigger>
      <PopoverContent
        side={popoverDirection}
        align="center"
        collisionPadding={8}
        sticky="partial"
        className={cn(
          'z-50 w-auto',
          'max-w-[var(--radix-popover-content-available-width)]',
          'max-h-[var(--radix-popover-content-available-height)]',
          'bg-white rounded-xl shadow-lg p-8 flex relative items-center justify-center',
        )}>
        <img
          onClick={(e) => e.stopPropagation()}
          src={bildUrl}
          alt={bildName}
          className="rounded-xl object-contain max-w-[40vw] max-h-[40vh]"
        />
        <Button
          variant="ghost"
          onClick={() => setIsOpen(false)}
          aria-label="Close"
          className="absolute top-1 right-1 hover:bg-transparent z-20">
          <FontAwesomeIcon icon={faXmark} size="lg" />
        </Button>
      </PopoverContent>
    </Popover>
  );
}
